import React, { useEffect , useContext } from 'react';
import L from 'leaflet'
import "leaflet.vectorgrid"
import { useMap } from 'react-leaflet';
import { Context } from '../store/reducer';

/**
 * ベクタタイルを地図上に描画し、クリックでストアに情報を反映するコンポーネント。
 *
 * 【実装の元になったコンポーネント】
 * - VectorGrid77_3 … 複数タイルセットの重ね描画とスタイル指定をそのまま踏襲している。
 * - VectorGrid90   … クリック時の dispatch 処理（TOGGLE_CHECKED / SET_VLAYER_INFO）を移植している。
 *                    id によるスタイル分岐は vis_90_4 では不要なため移植していない。
 *
 * 元データが複数のタイルセットに分割されているため、最大3組のタイルを受け取り、
 * 指定された組の数だけレイヤーを重ねて表示する。
 * url と layerName は対になっており、2組目は url2 / layerName2、
 * 3組目は url3 / layerName3 として、必ずセットで指定する。
 * 重ねたレイヤーはすべて同一のスタイル（color / borderColor）で描画される。
 *
 * 描画の透明度は props ではなくストア（state.opacity）から取得する。
 * DOMは出力せず、副作用として地図にレイヤーを追加するだけのコンポーネント。
 *
 * @param {Object} props
 * @param {string} props.id           レイヤーの識別子。クリック時の dispatch に使用する。
 * @param {string} props.url          1組目のベクタタイルのURLテンプレート
 * @param {string} props.layerName    1組目のタイル内のレイヤー名。スタイルを紐付けるキーとして使用する。
 * @param {string} [props.url2]       2組目のベクタタイルURL。省略した場合は描画しない。
 * @param {string} [props.layerName2] 2組目のタイル内のレイヤー名。url2 とセットで指定する。
 * @param {string} [props.url3]       3組目のベクタタイルURL。省略した場合は描画しない。
 * @param {string} [props.layerName3] 3組目のタイル内のレイヤー名。url3 とセットで指定する。
 * @param {string} props.color        ポリゴンの塗り色（全レイヤー共通）
 * @param {string} props.borderColor  ポリゴンの枠線の色（全レイヤー共通）
 * @returns {null} 描画するDOMは持たない
 */
const VectorGrid90_4 = ({id, url, layerName, url2, layerName2, url3, layerName3, color, borderColor}) => {
    const map = useMap()
    const { state, dispatch } = useContext(Context);

    useEffect(() => {
        // スタイルは全レイヤー共通のため、1つの関数を使い回す
        const getStyle = () => {
            return {
                color: borderColor,// 線
                opacity: state.opacity,
                fill: true,
                fillColor: color,
                dashArray: 0, // 0 = 実線
                fillOpacity: state.opacity
            }
        }

        // url と layerName が揃っている組だけを対象とし、その数だけレイヤーを重ねる
        const tileLayers = [
            { url: url,  layerName: layerName  },
            { url: url2, layerName: layerName2 },
            { url: url3, layerName: layerName3 },
        ].filter((tileLayer) => {
            return Boolean(tileLayer.url) && Boolean(tileLayer.layerName);
        });

        const vectorGrids = tileLayers.map((tileLayer) => {
            // vectorTileLayerStyles のキーは組ごとに異なるため、設定は組ごとに生成する
            const vectorGrid = L.vectorGrid.protobuf(tileLayer.url, {
                interactive:true,
                vectorTileLayerStyles: {
                    [tileLayer.layerName]: getStyle
                },
            })

            // 分割された全レイヤーが同一の id を持つため、どの組をクリックしても同じ扱いになる
            vectorGrid.on('click', (e) => {
                if (!e.originalEvent.isTrusted) {
                    dispatch({
                        type: 'TOGGLE_CHECKED',
                        category: id
                    })

                    dispatch({
                        type: 'SET_VLAYER_INFO',
                        id: id,
                        properties: e.layer.properties
                    })
                }
            });

            vectorGrid.addTo(map)

            return vectorGrid
        })

        // アンマウント時・再生成時に、追加した全レイヤーを地図から取り除く
        return () => {
            vectorGrids.forEach((vectorGrid) => {
                vectorGrid.remove()
            });
        }

        // url系・layerName系・id は文字列のため値で比較される。差し替わったらレイヤーを作り直す。
    }, [state.opacity, id, url, layerName, url2, layerName2, url3, layerName3])

    return null
}

export default VectorGrid90_4
